// Real liquid glass via liquid-gl (WebGPU -> WebGL -> CSS backdrop-filter).
//
// Structure:  <Tag.glass>                     interactive element, position: relative
//               <span.glass__pane/>           liquid-gl target (refraction shape)
//               <span.glass__content/>        label/links ABOVE the lens canvas
// liquid-gl takes over its target (pointer-events: none, fade-in opacity) and
// draws the refraction in a canvas at `zIndex`; the content sits above that
// canvas, so text stays crisp and clickable and is never refracted itself.
// `snapshot` limits what the lens sees: the hero button only refracts the hero
// photo, so letters of neighbouring sections can never appear inside it.
import { useEffect, useId, useRef, type ElementType, type ReactNode } from 'react';

type Lens = { destroy?: () => void };

interface GlassProps {
  as?: ElementType;
  snapshot?: string;
  className?: string;
  contentClassName?: string;
  children: ReactNode;
  tint?: string;
  zIndex: number;
  frost?: number;
  refreshKey?: string;
  [rest: string]: unknown;
}

const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export function Glass({
  as: Tag = 'div', snapshot = 'body', className = '', contentClassName = '', children,
  tint = 'rgba(12, 14, 20, 0.30)', zIndex, frost = 1.5, refreshKey, ...rest
}: GlassProps) {
  const id = `glass-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    let lens: Lens | Lens[] | undefined;
    let cancelled = false;
    const host = ref.current;
    const start = () =>
      import('liquid-gl')
        .then(({ default: liquidGL }) => {
          if (cancelled || !document.querySelector(`.${id}`)) return;
          lens = liquidGL({
            target: `.${id}`,
            snapshot,
            content: false,
            zIndex,
            resolution: Math.min(window.devicePixelRatio || 1, 2),
            refraction: 0.014,
            bevelDepth: 0.07,
            bevelWidth: 0.22,
            frost,
            shadow: true,
            specular: !reducedMotion(),
            reveal: reducedMotion() ? 'none' : 'fade',
            tint,
            on: {
              init() {
                host?.setAttribute('data-lens', 'ready');
              },
            },
          }) as Lens | Lens[] | undefined;
        })
        .catch(() => {
          /* CSS fallback (backdrop-filter) stays */
        });
    // let images and fonts paint first: the lens snapshots what is on screen
    const t = window.setTimeout(() => {
      if (document.readyState === 'complete') void start();
      else window.addEventListener('load', () => void start(), { once: true });
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
      (Array.isArray(lens) ? lens : lens ? [lens] : []).forEach((l) => l.destroy?.());
      host?.removeAttribute('data-lens');
    };
  }, [id, snapshot, tint, zIndex, frost, refreshKey]);

  return (
    <Tag ref={ref} className={`glass ${className}`} {...rest}>
      <span className={`glass__pane ${id}`} aria-hidden />
      <span className={`glass__content ${contentClassName}`}>{children}</span>
    </Tag>
  );
}
